/**
 * Frontend-only access list for the AI assistant (the ChatBubble and the
 * dashboard AI briefing strip).
 *
 * Driven by REACT_APP_AI_ASSISTANT_EMAILS — a comma-separated list of emails:
 *
 *   REACT_APP_AI_ASSISTANT_EMAILS=alice@example.com,bob@example.com
 *
 * .env files are plain text, so array/quote syntax is not parsed as JS. We
 * strip surrounding [ ] and per-entry quotes anyway, so this is equivalent:
 *
 *   REACT_APP_AI_ASSISTANT_EMAILS=['alice@example.com','bob@example.com']
 *
 * Only those emails see the assistant. Unset or empty means nobody gets it —
 * that is the intended "off for everyone" state, not a bug. Matching is
 * case-insensitive and ignores surrounding whitespace.
 *
 * To open the assistant to EVERY logged-in user, use the wildcard value
 * instead of listing emails:
 *
 *   REACT_APP_AI_ASSISTANT_EMAILS=all
 *
 * "all", "*", and "everyone" all mean the same thing, and the wildcard wins
 * wherever it appears in the list — `all,alice@example.com` is still everyone.
 * That also means a stray "all" entry silently opens the feature up, so keep
 * the value to either the wildcard or real emails, never a mix.
 *
 * CRA inlines REACT_APP_* at build time, so changing this needs a rebuild
 * (and a dev-server restart locally), not just a page refresh.
 *
 * NOTE: this is a UI gate only. The backend /ai/* routes are still reachable
 * by anyone with a valid token, so this is for controlled rollout, not
 * security. Enforce it server-side too when the feature goes wider.
 */
export const AI_ASSISTANT_ALLOWED_EMAILS: string[] = (
	process.env.REACT_APP_AI_ASSISTANT_EMAILS || ''
)
	.trim()
	// Tolerate a bracketed value: ['a@b.com','c@d.com']
	.replace(/^\[/, '')
	.replace(/\]$/, '')
	.split(',')
	// Tolerate per-entry single, double, or backtick quotes.
	.map((email) => email.trim().replace(/^['"`]|['"`]$/g, '').trim())
	.filter(Boolean);

const normalize = (email: string) => email.trim().toLowerCase();

// Spellings of "everyone" we accept, so nobody has to guess which one this
// file wanted. Anything else is treated as a literal email to match.
const WILDCARD_ENTRIES = ['all', '*', 'everyone'];

/**
 * True when the env value opens the assistant to every logged-in user rather
 * than a named list.
 */
export const isAiAssistantOpenToEveryone = (): boolean =>
	AI_ASSISTANT_ALLOWED_EMAILS.some((entry) =>
		WILDCARD_ENTRIES.includes(normalize(entry))
	);

export const isAiAssistantAllowed = (email?: string | null): boolean => {
	// Checked before the email guard on purpose: with the wildcard set, the
	// assistant should be there for everyone from the first render, including
	// the brief window before the user profile (and its email) has loaded.
	if (isAiAssistantOpenToEveryone()) {
		return true;
	}
	if (!email) {
		return false;
	}
	const target = normalize(email);
	return AI_ASSISTANT_ALLOWED_EMAILS.some(
		(allowedEmail) => normalize(allowedEmail) === target
	);
};
